import { FieldMetadata } from 'components/renderers/types';
import { EntityModel } from 'store/schema/types';

import { ConnectorSchemaResponse } from './types';

export const KNOWN_ENTITY_KEYS = new Set<string>([
  'id',
  'apiName',
  'description',
  'displayName',
  'subLabel',
  'iconPath',
  'pipelineStatus',
  'type',
  'connectedTo',
  'fields',
  'activeFields',
  'location',
  'status',
  'draftStatus',
  'createdBy',
  'createdAt',
  'updatedAt',
  'readonly',
  'child',
  'syncariSource',
  'deletedRecords',
  'totalFields',
  'totalRecords',
  'references',
  'lastUpdated',
  'updatedBy',
  'usedIn',
  'sources',
  'destinations',
  'tags',
  'hasDraft',
  'dataStoreName',
  'runDFI',
  'runMerge',
  'backfillStatus',
  'entityConfigValues',
  'entityConfigurations',
  'additionalProperties',
]);

type DynamicEntityProperty = {
  name: string;
  label: string;
  dataType: string;
};

export type DynamicEntityPropertyInput = DynamicEntityProperty & {
  value: unknown;
};

export const isBooleanDynamicEntityProperty = (property: Pick<DynamicEntityProperty, 'dataType'>): boolean =>
  String(property.dataType).toLowerCase() === 'boolean';

export const getDynamicEntityPropertyValue = (property: DynamicEntityPropertyInput): string | boolean =>
  isBooleanDynamicEntityProperty(property) ? Boolean(property.value) : String(property.value ?? '');

export const getUpdatedDynamicEntityProperties = (
  entityConfigValues: Record<string, unknown> | undefined,
  property: DynamicEntityPropertyInput,
  nextValue: unknown
): Record<string, unknown> => ({
  ...(entityConfigValues || {}),
  [property.name]: isBooleanDynamicEntityProperty(property) ? Boolean(nextValue) : String(nextValue ?? ''),
});

// Every key in the bag is sent, empty strings included, so clearing a config value is preserved.
export const normalizeDynamicEntityPropertiesForSave = (
  entityConfigValues: Record<string, unknown> | undefined
): Record<string, unknown> => ({ ...(entityConfigValues || {}) });

// Backward compatibility: if a response still carries dynamic entity fields at top level,
// preserve keys already owned by additionalProperties/entityConfigValues and only fall back to entityConfigValues
// for unknown legacy dynamic fields.
export const hydrateDynamicProperties = (entity: Partial<EntityModel>): Partial<EntityModel> => {
  const nextEntityConfigValues: Record<string, unknown> = { ...(entity.entityConfigValues || {}) };
  const nextAdditionalProperties: Record<string, unknown> = { ...(entity.additionalProperties || {}) };
  const nextEntity: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(entity)) {
    if (KNOWN_ENTITY_KEYS.has(key)) {
      nextEntity[key] = value;
    } else if (Object.prototype.hasOwnProperty.call(nextAdditionalProperties, key)) {
      nextAdditionalProperties[key] = value;
    } else if (Object.prototype.hasOwnProperty.call(nextEntityConfigValues, key)) {
      nextEntityConfigValues[key] = value;
    } else {
      nextEntityConfigValues[key] = value;
    }
  }

  nextEntity.entityConfigValues = nextEntityConfigValues;
  nextEntity.additionalProperties = nextAdditionalProperties;
  return nextEntity as Partial<EntityModel>;
};

export const getDynamicEntityProperties = (
  connectorSchema: ConnectorSchemaResponse | null | undefined,
  entityApiName: string | undefined,
  entityConfigValues: Record<string, unknown> = {}
): DynamicEntityPropertyInput[] => {
  if (!connectorSchema?.data?.length || !entityApiName) {
    return [];
  }

  const schemaEntry = connectorSchema.data.find((entry) => entry.apiName === entityApiName);
  // The draft is the working copy: once an entity has one, it is the only source for dynamic
  // property values. Falling through to the published entity would resurrect a value the user
  // cleared on the draft, since clearing a property is expressed as its absence or an empty string.
  const storedFields = (schemaEntry?.draft?.fields || schemaEntry?.published?.fields || {}) as Record<string, unknown>;
  const fieldNames = Object.keys(connectorSchema.meta || {}).filter((fieldName) => !KNOWN_ENTITY_KEYS.has(fieldName));

  return fieldNames.map((fieldName) => {
    const meta = connectorSchema.meta?.[fieldName as keyof typeof connectorSchema.meta] as FieldMetadata | undefined;
    const dataType = meta?.dataType || 'string';
    // In-flight edits win, and they win even when the edit cleared the value.
    const storedEntityConfigValues = (storedFields.entityConfigValues || {}) as Record<string, unknown>;
    const value = Object.prototype.hasOwnProperty.call(entityConfigValues, fieldName)
      ? entityConfigValues[fieldName]
      : Object.prototype.hasOwnProperty.call(storedEntityConfigValues, fieldName)
      ? storedEntityConfigValues[fieldName]
      : storedFields[fieldName];

    return {
      name: fieldName,
      label: meta?.value || fieldName,
      dataType,
      value,
    };
  });
};
